import { Component } from 'react'
import { Link } from 'react-router-dom'
import { ErrorMessage, emptyField, errorsLength, formatErrors, validatePasswordConfirmation, validateEmail } from '../../../../insfrastructure/utils/form-validators'
import { requestOTP } from '../../../../insfrastructure/services/auth'
import { handleResendVerificationEmail } from '../../../../insfrastructure/utils/email-verification-helpers'
import { parseJwt } from '../../../../insfrastructure/utils/JWTParser'
import { getAuthToken, clearAllTokens } from '../../../../insfrastructure/services/token-manager'
import { retrieveCandidate } from '../../../../insfrastructure/services/candidates'
import { retrieveCompanyManager } from '../../../../insfrastructure/services/companies'
import { withRouter } from '../../../../insfrastructure/utils/with-router-component'
import { Input } from '../../../components/input'
import { SubmitBtn } from '../../../components/submit'
import ResponseModal from '../../../components/response-modal'
import { PasswordRequirements } from '../../../components/password-requirements'
import { handleEmailNotValidatedError } from '../../../../insfrastructure/utils/email-not-validated-handler'
import forgotPasswordImg from '../../../../assets/images/forgot_password.png'
import checkImg from '../../../../assets/images/check.png'
import CheckboxInput from '../../../components/checkbox'
import axios from 'axios'
import './styles.sass'


class ResetPassword extends Component<any> {
    state: any = {
        form: {
            email: '',
            new_password1: '',
            new_password2: ''
        },
        formErrors: {},
        loading: false,
        sended: false,
        userdata: {},
        resendingEmail: false,
        responseData: null,
        emailValidated: false,
        otpAlreadyRequested: false // Flag para rastrear se OTP já foi solicitado durante validação
    }

    async componentDidMount() {
        const authToken = getAuthToken();
        
        // Se o usuário está logado, buscar dados primeiro
        if (authToken) {
            const parsedJWT = parseJwt(authToken);

            if (parsedJWT) {
                let userdata: any = {};

                if (parsedJWT.candidate_id) {
                    userdata = await retrieveCandidate();
                } else {
                    userdata = await retrieveCompanyManager();
                    userdata.email = userdata.manager_email;
                };

                // Se o usuário está logado, já considera o email validado
                this.setState({ userdata, emailValidated: !!userdata.email });
                return; // Se está logado, não precisa processar email do state
            }
        }

        // Verificar se veio email do state da navegação (de /forgot-password)
        // Só processa se o usuário não estiver logado
        const emailFromState = this.props.location?.state?.email;
        if (emailFromState) {
            this.setState({ 
                form: { ...this.state.form, email: emailFromState },
                emailValidated: true, // Se veio de forgot-password, já foi validado lá
                otpAlreadyRequested: true // OTP já foi solicitado em /forgot-password
            });
        }
    }

    handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            if (!this.state.emailValidated && !this.state.userdata.email) {
                this.onValidateEmail();
            } else {
                this.onSubmit();
            }
        }
    }

    handleResendVerificationEmail = async () => {
        // Prevenir múltiplos cliques
        if (this.state.resendingEmail) {
            return;
        }

        const email = !this.state.userdata.email ? this.state.form.email : this.state.userdata.email;

        await handleResendVerificationEmail({
            email,
            setResendingEmail: (value) => this.setState({ resendingEmail: value }),
            setResponseData: (data) => this.setState({ responseData: data }),
            invalidEmailMessage: 'Por favor, informe um email válido.'
        });
    }

    onValidateEmail = async () => {
        // Só valida email se o usuário não estiver logado
        if (this.state.userdata.email) {
            this.setState({ emailValidated: true });
            return;
        }

        this.setState({ formErrors: {}, responseData: null });
        const errors = formatErrors({
            email: [validateEmail(this.state.form.email)]
        });

        if(errorsLength(errors) > 0) {
            this.setState({ formErrors: errors });
            return;
        }

        this.setState({ loading: true });

        try {
            // Tentar solicitar OTP para verificar se o email está validado
            await requestOTP({ email: this.state.form.email });
            
            // Se chegou aqui, o email está validado e OTP já foi enviado
            this.setState({ 
                emailValidated: true, 
                loading: false,
                otpAlreadyRequested: true // Marcar que OTP já foi solicitado
            });
        } catch (error: any) {
            let errorMessage = 'Ocorreu um erro inesperado. Tente novamente em instantes.';
            let errorTitle = 'Não foi possível verificar o email';
            let errorTextComponent: (() => JSX.Element) | null = null;

            // Verificar se é erro de email não validado usando handler reutilizável
            const emailNotValidatedResult = handleEmailNotValidatedError({
                error,
                resendingEmail: this.state.resendingEmail,
                onResend: () => this.handleResendVerificationEmail(),
                defaultMessage: 'Por favor, confirme seu email antes de redefinir a senha.',
                context: 'reset_password',
                allowedStatusCodes: [401, 403]
            });
            
            if (emailNotValidatedResult.isEmailNotValidated) {
                errorTitle = emailNotValidatedResult.errorTitle;
                errorTextComponent = emailNotValidatedResult.errorTextComponent;
            } else if (axios.isAxiosError(error)) {
                const status = error.response?.status;
                const backendMessage = error.response?.data?.message;
                const backendCode = error.response?.data?.code;

                if (status === 404 || backendCode === 'user_not_found') {
                    errorMessage = backendMessage || 'Não encontramos uma conta com este e-mail. Verifique se digitou corretamente ou tente outro endereço.';
                } else if (backendMessage) {
                    errorMessage = backendMessage;
                }
            }

            const responseData = {
                type: 'error',
                title: errorTitle,
                text: errorTextComponent || (() => <p>{errorMessage}</p>)
            };

            this.setState({ responseData, loading: false });
        }
    }

    onSubmit = async () => {
        // Se o email ainda não foi validado e o usuário não está logado, validar primeiro
        if (!this.state.emailValidated && !this.state.userdata.email) {
            await this.onValidateEmail();
            return;
        }

        this.setState({ formErrors: {} });

        let fields: Record<string, any> = {
            new_password1: [emptyField(this.state.form.new_password1, 'string')],
            new_password2: [validatePasswordConfirmation(this.state.form.new_password1, this.state.form.new_password2)]
        };

        if (!this.state.userdata.email) {
            fields['email'] = [emptyField(this.state.form.email, 'string')];
        } else {
            fields['terms_and_conditions'] = [emptyField(this.state.form.terms_and_conditions, 'string')];
        };

        const errors = formatErrors(fields);

        this.setState({ formErrors: errors }, () => {
            if (errorsLength(errors) > 0) {
                const firstErrorElement = document.querySelector('.errors .error') as HTMLElement | null;
                if (firstErrorElement && typeof firstErrorElement.scrollIntoView === 'function') {
                    firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        });

        if(errorsLength(errors) > 0 || !this.state.isPasswordValidated) return;

        this.setState({ loading: true });

        try {
            // Só solicitar OTP se ainda não foi solicitado durante a validação do email
            // Se o usuário está logado, sempre solicita (não passou por onValidateEmail)
            // Se otpAlreadyRequested é true, não solicita novamente (já foi solicitado em onValidateEmail ou veio de /forgot-password)
            const shouldRequestOTP = !this.state.otpAlreadyRequested || this.state.userdata.email;
            
            if (shouldRequestOTP) {
                await requestOTP({ email: !this.state.userdata.email ? this.state.form.email : this.state.userdata.email });
            }

            setTimeout(() => {
                this.setState({ sended: true, loading: false });
                this.props.navigate('/validate-token', {
                    state: {
                        email: !this.state.userdata.email ? this.state.form.email : this.state.userdata.email,
                        new_password1: this.state.form.new_password1,
                        new_password2: this.state.form.new_password2,
                        fromProfile: this.props.location?.state?.fromProfile || false
                    }
                });
            });
        } catch (error: any) {
            let errorMessage = 'Ocorreu um erro inesperado. Tente novamente em instantes.';
            let errorTitle = 'Não foi possível enviar o e-mail';
            let errorTextComponent: (() => JSX.Element) | null = null;

            // Verificar se é erro de email não validado usando handler reutilizável
            const emailNotValidatedResult = handleEmailNotValidatedError({
                error,
                resendingEmail: this.state.resendingEmail,
                onResend: () => this.handleResendVerificationEmail(),
                defaultMessage: 'Por favor, confirme seu email antes de redefinir a senha.',
                context: 'reset_password',
                allowedStatusCodes: [401, 403]
            });
            
            if (emailNotValidatedResult.isEmailNotValidated) {
                errorTitle = emailNotValidatedResult.errorTitle;
                errorTextComponent = emailNotValidatedResult.errorTextComponent;
            } else if (axios.isAxiosError(error)) {
                const status = error.response?.status;
                const backendMessage = error.response?.data?.message;
                const backendCode = error.response?.data?.code;

                if (status === 404 || backendCode === 'user_not_found') {
                    errorMessage = backendMessage || 'Não encontramos uma conta com este e-mail. Verifique se digitou corretamente ou tente outro endereço.';
                } else if (backendMessage) {
                    errorMessage = backendMessage;
                }
            }

            const responseData = {
                type: 'error',
                title: errorTitle,
                text: errorTextComponent || (() => <p>{errorMessage}</p>)
            };

            clearAllTokens();
            this.setState({ responseData, loading: false });
        };
    }

    render() {
        return (
            <div className='reset-password unlogged-page'>
                <div className='content'>
                    <div className='block'>
                        {window.innerWidth > 1000 &&
                        <div className='img-container'>
                            <img src={forgotPasswordImg} alt='forgot-password-image' />
                        </div>}
                        {
                            !this.state.sended ?
                            <div className='block-content not-sended'>
                                <div className='text'>
                                    <h3>Olá{this.state.userdata?.full_name ? ' '+this.state.userdata?.full_name?.split(' ')[0] : ''}! A seguir altere sua senha</h3>
                                </div>
                                <div className='form'>
                                    {
                                        !this.state.userdata.email &&
                                        <>
                                            <div className='email-label-container'>
                                                <label>Email</label>
                                                <Link to='/forgot-email' className='forgot-email-link'>Esqueci meu email</Link>
                                            </div>
                                            <Input
                                                name='email'
                                                placeholder='Insira seu email'
                                                value={this.state.form.email}
                                                onChange={e => this.setState({ form: { ...this.state.form, email: e }, formErrors: { ...this.state.formErrors, email: [] } })}
                                                onKeyDown={this.handleKeyDown}
                                                disabled={this.state.loading || this.state.emailValidated}/>
                                            <ErrorMessage errors={this.state.formErrors['email']} />
                                        </>
                                    }
                                    {
                                        (this.state.emailValidated || this.state.userdata.email) &&
                                        <>
                                            <Input
                                            name='new_password1'
                                            label='Nova Senha'
                                            type='password'
                                            placeholder='Insira sua nova senha'
                                            onChange={e => this.setState({ form: {...this.state.form, new_password1: e}, formErrors: { ...this.state.formErrors, new_password1: [] } })}/>
                                            <ErrorMessage errors={this.state.formErrors['new_password1']} />
                                            <Input
                                            name='new_password2'
                                            label='Confirme sua Nova Senha'
                                            type='password'
                                            placeholder='Insira sua nova senha'
                                            onChange={e => this.setState({ form: {...this.state.form, new_password2: e}, formErrors: { ...this.state.formErrors, new_password2: [] } })}
                                            onKeyDown={this.handleKeyDown}/>
                                            <ErrorMessage errors={this.state.formErrors['new_password2']} />
                                            {
                                                this.state.userdata.email && 
                                                <div className='terms-and-conditions-container'>
                                                    <CheckboxInput
                                                        name='terms_and_conditions'
                                                        label={
                                                          () => (
                                                            <div>
                                                              Aceito a{' '}
                                                              <a 
                                                                href='https://empregamecanico.com.br/privacy-policy' 
                                                                target='_blank' 
                                                                rel='noopener noreferrer'
                                                                style={{ color: '#FFD700', textDecoration: 'underline', fontWeight: '600' }}
                                                              >
                                                                Política de Privacidade
                                                              </a>
                                                            </div>
                                                          )
                                                        }
                                                        checked={this.state.form.terms_and_conditions}
                                                        onChange={e => this.setState({ form: { ...this.state.form, terms_and_conditions: e }, formErrors: { ...this.state.formErrors, terms_and_conditions: [] } })}/>
                                                    <ErrorMessage errors={this.state.formErrors['terms_and_conditions']} />
                                                </div>
                                            }
                                            <PasswordRequirements
                                            password={this.state.form.new_password1}
                                            email={!this.state.userdata.email ? this.state.form.email : this.state.userdata.email}
                                            isPasswordValidated={isPasswordValidated => this.setState({ isPasswordValidated })}/>
                                        </>
                                    }
                                </div>
                                {/* <p className='bottom-text'>Ao <b>confirmar</b>, sua <b>senha</b> será alterada.</p> */}
                                <SubmitBtn 
                                    className='secondary' 
                                    text={!this.state.emailValidated && !this.state.userdata.email ? 'Continuar' : 'Confirmar'} 
                                    loadingText={!this.state.emailValidated && !this.state.userdata.email ? 'Verificando...' : 'Enviando...'} 
                                    loading={this.state.loading} 
                                    onSubmit={this.onSubmit} 
                                />
                            </div> :
                            <div className='block-content sended'>
                                <div className='text'>
                                    <img className='check' src={checkImg} alt='check-image' />
                                    <h3>Senha alterada com sucesso!</h3>
                                </div>
                                <Link to='/login'><SubmitBtn className='secondary' text='OK' /></Link>
                            </div>
                        }
                    </div>
                </div>
                {this.state.responseData && (
                <ResponseModal
                    render={!!this.state.responseData}
                    type={this.state.responseData.type}
                    title={this.state.responseData.title}
                    text={this.state.responseData.text}
                onClose={() => this.setState({ responseData: null })} />
                )}
            </div>
        )
    }
}

export default withRouter(ResetPassword);